import { QuickStatsCardProps } from "@features/cx/shared/types/quickStatsCardProps";
import { Card, CardContent } from "@shared/ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@shared/ui/tooltip";
import { ArrowDown, ArrowUp, Info } from "lucide-react";

export function QuickStatsCard({
  title,
  count,
  displayValue,
  trend,
  trendDurationDisplay,
  trendSummary,
  icon,
  tooltip,
  badge,
  badgeColor,
  className = "",
}: QuickStatsCardProps & { className?: string }) {
  const trendDirectionStat = trend > 0 ? "up" : trend < 0 ? "down" : "neutral";
  const trendDurationDisplayText = trendDurationDisplay ?? "from yesterday";

  const shouldShowTrendTooltip =
    typeof trend !== "number" && trendSummary && trendSummary.length > 35;

  return (
    <Card className={className}>
      <CardContent className="pt-0">
        <div className="flex justify-between">
          <div className="flex-1 min-w-0 pr-4">
            {/* Title + Tooltip + Badge */}
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1">
                <p className="text-sm font-medium text-muted-foreground">
                  {title}
                </p>

                {tooltip && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger>
                        <Info className="h-3 w-3 text-gray-400" />
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs bg-white border shadow-lg">
                        <p className="text-sm">{tooltip}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </div>

              {badge && (
                <span
                  className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                    badgeColor || "bg-gray-100 text-gray-800"
                  }`}
                >
                  {badge}
                </span>
              )}
            </div>

            {/* Value */}
            <p className="text-lg font-bold mt-1">{displayValue ?? count}</p>

            {/* Trend Section */}
            <div className="flex items-center mt-1">
              {typeof trend === "number" ? (
                trendDirectionStat !== "neutral" ? (
                  <span
                    className={`flex items-center text-xs ${
                      trendDirectionStat === "up"
                        ? "text-green-500"
                        : "text-red-500"
                    }`}
                  >
                    {trendDirectionStat === "up" ? (
                      <ArrowUp className="h-3 w-3 mr-1" />
                    ) : (
                      <ArrowDown className="h-3 w-3 mr-1" />
                    )}
                    {trend}% {trendDurationDisplayText}
                  </span>
                ) : (
                  <span className="text-xs text-muted-foreground">
                    0 {trendDurationDisplayText}
                  </span>
                )
              ) : (
                <>
                  {/* Trend Summary with 2-line clamp + Tooltip */}
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="text-xs text-muted-foreground line-clamp-2 cursor-pointer max-w-[160px]">
                          {trendSummary}
                        </span>
                      </TooltipTrigger>

                      {shouldShowTrendTooltip && (
                        <TooltipContent className="max-w-xs bg-white border shadow-lg">
                          <p className="text-sm">{trendSummary}</p>
                        </TooltipContent>
                      )}
                    </Tooltip>
                  </TooltipProvider>
                </>
              )}
            </div>
          </div>

          {/* Icon */}
          <div className="w-12 h-12 rounded-lg flex items-center justify-center">
            <div>{icon}</div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
